import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SEOHead } from '../seo';
import { SearchBar, FilterSection, TherapistCard } from '../common';
import { seoConfigs } from '../../utils/seo-configs';
import { endpoints } from '../../config/api';

function FindTherapistsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchContext, setSearchContext] = useState(null);
  
  // Advanced filter states
  const [filters, setFilters] = useState({
    specialty: '',
    insurance: '',
    sessionType: '',
    radius: '50',
    state: '',
    city: '',
    gender: '',
    professionalType: ''
  });

  // Ref to track when filters change so we can reset to page 1
  const filtersChangedRef = useRef(false);



  // Parse URL query parameters
  const parseQueryParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      location: params.get('location'),
      lat: params.get('lat'),
      lng: params.get('lng'),
      specialty: params.get('specialty'),
      q: params.get('q'),
      page: parseInt(params.get('page')) || 1
    };
  };

  // Get current search parameters
  const queryParams = parseQueryParams();

  // Determine search context for display
  useEffect(() => {
    const params = parseQueryParams();
    if (params.lat && params.lng) {
      setSearchContext({
        type: 'geolocation',
        value: `${params.lat},${params.lng}`,
        display: 'Therapists near your current location'
      });
    } else if (params.location) {
      setSearchContext({
        type: 'location',
        value: params.location,
        display: `Therapists near "${params.location}"`
      });
    } else if (params.specialty) {
      setSearchContext({
        type: 'specialty',
        value: params.specialty,
        display: `${params.specialty.charAt(0).toUpperCase() + params.specialty.slice(1)} Therapists`
      });
    } else if (params.q) {
      setSearchContext({
        type: 'general',
        value: params.q,
        display: `Search results for "${params.q}"`
      });
    } else {
      setSearchContext({
        type: 'browse',
        value: '',
        display: 'Browse All Therapists'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Fetch therapists based on current parameters
  const fetchTherapists = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        sort: 'ranking',
        radius: filters.radius
      });

      // Add search parameters
      const queryParams = parseQueryParams();
      if (queryParams.lat && queryParams.lng) {
        params.append('lat', queryParams.lat);
        params.append('lng', queryParams.lng);
      } else if (queryParams.location) {
        params.append('location', queryParams.location);
      }
      if (queryParams.q) {
        params.append('q', queryParams.q);
      }

      // Add specialty - filter takes precedence over URL param to avoid duplicates
      if (filters.specialty && filters.specialty !== '') {
        params.append('specializations', filters.specialty);
      } else if (queryParams.specialty) {
        params.append('specializations', queryParams.specialty);
      }
      if (filters.insurance && filters.insurance !== '') {
        params.append('insurance', filters.insurance);
      }
      if (filters.sessionType && filters.sessionType !== '') {
        params.append('sessionType', filters.sessionType);
      }
      if (filters.state && filters.state !== '') {
        params.append('state', filters.state);
      }
      if (filters.city && filters.city !== '') {
        params.append('city', filters.city);
      }
      if (filters.gender && filters.gender !== '') {
        params.append('gender', filters.gender);
      }
      if (filters.professionalType && filters.professionalType !== '') {
        params.append('professionalType', filters.professionalType);
      }

      const response = await fetch(`${endpoints.search.therapists}?${params}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch therapists: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setTherapists(data.therapists || []);
        setTotalResults(data.pagination?.totalCount || 0);
        setTotalPages(data.pagination?.totalPages || 0);
        setCurrentPage(page);
        
        // Track search impressions for all therapists shown
        if (data.therapists && data.therapists.length > 0) {
          const therapistIds = data.therapists.map(t => t._id);
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/analytics/search-impression`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ therapistIds }),
          }).catch(error => {
            console.warn('Failed to track search impressions:', error);
          });
        }
        
        // Check if there's a location-not-found message
        if (data.message && data.therapists.length === 0) {
          setError(data.message);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch therapists');
      }
    } catch (err) {
      console.error('Error fetching therapists:', err);
      setError(err.message);
      setTherapists([]);
    } finally {
      setLoading(false);
    }
  };



  // Initial load and when query params or filters change
  useEffect(() => {
    const params = parseQueryParams();
    // Reset to page 1 when filters change, otherwise use URL page
    const page = filtersChangedRef.current ? 1 : params.page;
    filtersChangedRef.current = false;
    fetchTherapists(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, filters]);

  // Handle search from SearchBar component
  const handleSearch = (searchQuery, searchData) => {
    if (searchData.type === 'location' && searchData.lat && searchData.lng) {
      // Handle geolocation search
      navigate(`/find-therapists?lat=${searchData.lat}&lng=${searchData.lng}`);
    } else if (searchData.type === 'city' && !searchData.needsState) {
      // Navigate to city page
      navigate(`/therapists/${searchData.stateUrl}/${searchData.cityUrl}`);
    } else if (searchData.type === 'specialty') {
      // Update current page with specialty search
      navigate(`/find-therapists?specialty=${encodeURIComponent(searchData.specialty)}`);
    } else if (searchData.type === 'city' && searchData.needsState) {
      // Search for city that needs state clarification
      navigate(`/find-therapists?location=${encodeURIComponent(searchQuery)}`);
    } else if (searchData.type === 'location' && searchData.zipCode) {
      // Handle ZIP code searches
      navigate(`/find-therapists?location=${encodeURIComponent(searchData.zipCode)}`);
    } else if (searchData.type === 'location' && searchData.city && searchData.state) {
      // Handle formatted location searches (like "City (State)" format)
      navigate(`/find-therapists?location=${encodeURIComponent(searchData.query)}`);
    } else if (searchData.type === 'state') {
      // Handle state searches - use location parameter
      navigate(`/find-therapists?location=${encodeURIComponent(searchData.state)}`);
    } else {
      // General search
      navigate(`/find-therapists?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    filtersChangedRef.current = true;
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setCurrentPage(1);
  };



  // Handle page change
  const handlePageChange = (page) => {
    const params = new URLSearchParams(location.search);
    params.set('page', page.toString());
    navigate(`${location.pathname}?${params.toString()}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Clear all filters and reset search
  const clearFilters = () => {
    // Reset filter state
    setFilters({
      specialty: '',
      insurance: '',
      sessionType: '',
      radius: '50',
      state: '',
      city: '',
      gender: '',
      professionalType: ''
    });
    
    // Reset URL to clear search parameters (location, q, specialty, etc.)
    navigate('/find-therapists');
  };

  return (
    <>
      <SEOHead {...seoConfigs.findTherapists} />
      
      <div className="min-h-screen bg-[#e4fde1]" style={{ fontFamily: 'Nunito Sans, sans-serif', marginTop: '-200px', paddingTop: '200px' }}>
        {/* Header Section */}
        <div className="border-b border-[#254e70]/10">
          <div className="max-w-7xl mx-auto px-4 py-8 lg:py-16">
            <div className="text-center space-y-4 lg:space-y-6">
              <h1 className="text-2xl lg:text-4xl xl:text-5xl font-medium text-[#254e70] leading-tight" style={{ fontFamily: 'Kanit, sans-serif' }}>
                {searchContext?.display || 'Find Your Perfect Therapist'}
              </h1>
              <p className="text-base lg:text-lg text-[#254e70]/80 max-w-3xl mx-auto">
                Search our directory of licensed mental health professionals. Filter by location, specialty, insurance, and more.
              </p>
              
              {/* Search Bar */}
              <div className="max-w-4xl mx-auto">
                <SearchBar 
                  placeholder="Search by location, specialty, or therapist name..."
                  onSearch={handleSearch}
                  initialValue={
                    (queryParams.lat && queryParams.lng) ? 'Current Location' :
                    queryParams.location || queryParams.specialty || queryParams.q || ''
                  }
                  size="large"
                />
              </div>
              
              {/* Results summary */}
              <div className="flex flex-wrap justify-center gap-2 lg:gap-4 text-xs lg:text-sm">
                <div className="hidden sm:block bg-white/80 px-3 lg:px-4 py-1.5 lg:py-2 rounded-full border border-[#254e70]/10">
                  <span className="text-[#254e70]/60">⭐</span> Verified Profiles
                </div>
                <div className="hidden md:block bg-white/80 px-3 lg:px-4 py-1.5 lg:py-2 rounded-full border border-[#254e70]/10">
                  <span className="text-[#254e70]/60">🔍</span> Advanced Filters
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto p-4 lg:p-8">
            <div className="lg:grid lg:grid-cols-4 lg:gap-8">
              {/* Filter Section - responsive mobile/desktop */}
              <div className="lg:col-span-1">
                <FilterSection
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={clearFilters}
                  showLocationFilters={true}
                />
              </div>

              {/* Main Content */}
              <div className="lg:col-span-3">

          {/* Results Summary */}
          <div className="mb-8">
            <p className="text-[#254e70]/70">
              {loading ? 'Loading...' : (
                <>
                  Showing therapists
                </>
              )}
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#254e70]"></div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-12">
              <h3 className="text-xl font-medium text-[#254e70] mb-4" style={{ fontFamily: 'Kanit, sans-serif' }}>
                Unable to Load Results
              </h3>
              <p className="text-[#254e70]/70 mb-6">{error}</p>
              <button 
                onClick={() => fetchTherapists(currentPage)}
                className="bg-[#254e70] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#1d3e5a] transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* No Results */}
          {!loading && !error && therapists.length === 0 && (
            <div className="text-center py-12">
              <h3 className="text-xl font-medium text-[#254e70] mb-4" style={{ fontFamily: 'Kanit, sans-serif' }}>
                No therapists found
              </h3>
              <p className="text-[#254e70]/70 mb-6">
                Try adjusting your search criteria or expanding your search radius.
              </p>
              <button 
                onClick={clearFilters}
                className="bg-[#254e70] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#1d3e5a] transition-colors"
              >
                Browse All Therapists
              </button>
            </div>
          )}

          {/* Therapist Results */}
          {!loading && !error && therapists.length > 0 && (
            <div className="space-y-6">
              {therapists.map((therapist) => (
                <TherapistCard
                  key={therapist._id}
                  therapist={therapist}
                  onViewProfile={(therapist) => {
                    if (therapist.slug) {
                      navigate(`/therapist/${therapist.slug}`);
                    } else {
                      console.log('No slug available for therapist:', therapist);
                    }
                  }}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && !error && totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-12">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-xl border border-[#254e70]/20 text-[#254e70] hover:bg-[#e4fde1] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ fontFamily: 'Nunito Sans, sans-serif' }}
              >
                Previous
              </button>
              
              {[...Array(Math.min(5, totalPages))].map((_, index) => {
                const page = Math.max(1, currentPage - 2) + index;
                if (page > totalPages) return null;
                
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                      currentPage === page
                        ? 'bg-[#254e70] text-white'
                        : 'border border-[#254e70]/20 text-[#254e70] hover:bg-[#e4fde1]'
                    }`}
                    style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-xl border border-[#254e70]/20 text-[#254e70] hover:bg-[#e4fde1] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ fontFamily: 'Nunito Sans, sans-serif' }}
              >
                Next
              </button>
            </div>
          )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default FindTherapistsPage; 